import {
  addSample,
  changeOrderCountOfSample,
  ProductVariationWithSample,
  removeSample,
  clearAllSamples,
  SampleWithQuantity,
  setQuestionnaire,
  clearQuestionnaire,
  setOrder as setOrderOnStore,
  clearOrder as clearOrderOnStore,
} from "@/stores/cart";
import { RootState } from "@/stores/rootReducer";
import {
  OrderWithGroupsResponse,
  OrderCreateParameterOrderItemsInner,
  OrderCreateParameterQuestionnaire,
  ProductResponse,
  SampleItemEntity,
  SampleRequestKind,
} from "@/web-client/api";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAnalytics from "./useAnalytics";
import useConvertSampleInCartToAnalyticsParam from "./useConvertSampleInCartToAnalyticsParam";
import { getMaxQuantityPerSkuErrorMessage } from "@/domain/values/ErrorMessage";

export type OrganizedSamples = {
  maker_id: number;
  samples: SampleWithQuantity[];
};

export type OutOfStockSample = {
  sample: SampleWithQuantity;
  currentStockCount: number;
};

export const getSampleIdentifier = (
  sample: SampleWithQuantity,
  direct: boolean,
) => {
  if (direct) {
    return String(sample.productVariation.id);
  }

  return sample.productVariation.sample_item
    ? String(sample.productVariation.sample_item.id)
    : "";
};

export type DuplicateCheckResult = { quantity: number; duplicate: boolean };

type UseCartReturn = {
  samplesInCart: SampleWithQuantity[];
  orderItems: OrderCreateParameterOrderItemsInner[];
  hasItems: boolean;
  questionnaire: OrderCreateParameterQuestionnaire;
  productVariationIds: number[];

  quantity: number;
  latestItem: SampleWithQuantity;
  order: OrderWithGroupsResponse;

  indirectQuantities: Record<string, number>;
  directQuantities: Record<string, number>;
  organizedDirect: OrganizedSamples[];
  organizedIndirect: OrganizedSamples[];

  addSampleToCart: (sample: SampleWithQuantity) => void;
  removeItemInCart: (
    productVariationWithSample: ProductVariationWithSample,
  ) => void;
  removeAllItemsInCart: VoidFunction;
  changeOrderCountOfSampleInCart: (
    productVariationWithSample: ProductVariationWithSample,
    product: ProductResponse,
    quantity: number,
  ) => void;
  setQuestionnaireForOrder: (
    questionnaire: OrderCreateParameterQuestionnaire,
  ) => void;

  setOrder: (order: OrderWithGroupsResponse) => void;
  clearOrder: VoidFunction;

  reset: VoidFunction;
  hasSameItem: (
    productVariationWithSample: ProductVariationWithSample,
  ) => boolean;
  hasSameSampleItem: (
    productVariationWithSample: ProductVariationWithSample,
  ) => boolean;

  getOutOfStockSamples: (samples: SampleItemEntity[]) => OutOfStockSample[];

  checkDuplicateBeforeAddToCart: (
    productVariation: ProductVariationWithSample,
  ) => DuplicateCheckResult;
};

// 共通の関数でサンプル数量を計算
const calculateQuantities = (
  samples: SampleWithQuantity[],
  getIdFn: (sample: SampleWithQuantity) => string,
): Record<string, number> => {
  // idをキーとして数量を合計する
  return samples.reduce(
    (acc, sample) => {
      const id = getIdFn(sample);

      if (!id) return acc;

      if (!acc[id]) {
        acc[id] = 0;
      }

      acc[id] += sample.quantity;

      return acc;
    },
    {} as Record<string, number>,
  );
};

// メーカーごとにサンプルをまとめる関数
const organizeSamplesByMakerId = (
  samples: SampleWithQuantity[],
): OrganizedSamples[] => {
  // makerIdでグループ化した後、配列に変換する
  const grouped = samples.reduce<Record<number, SampleWithQuantity[]>>(
    (acc, sample) => {
      const makerId = sample.product.maker_id;

      if (!acc[makerId]) {
        acc[makerId] = [];
      }

      acc[makerId].push(sample);

      return acc;
    },
    {},
  );

  // Record型から配列に変換する
  return Object.entries(grouped).map(([makerId, samples]) => ({
    maker_id: Number(makerId),
    samples,
  }));
};

const useCart = (): UseCartReturn => {
  const dispatch = useDispatch();
  const { event } = useAnalytics();
  const { convert } = useConvertSampleInCartToAnalyticsParam();
  const { samples, questionnaire, order } = useSelector(
    (state: RootState) => state.cart,
  );

  const addSampleToCart = useCallback(
    (sample: SampleWithQuantity) => {
      dispatch(addSample({ ...sample, updated_at: new Date().toISOString() }));
      event("add_to_cart", {
        maker_id: sample.product.maker_id,
        items: [convert(sample)],
      });
    },
    [dispatch, event, convert],
  );

  const changeOrderCountOfSampleInCart = useCallback(
    (productVariation, product, quantity) => {
      dispatch(
        changeOrderCountOfSample({
          productVariation,
          product,
          quantity,
          updated_at: new Date().toISOString(),
        }),
      );
    },
    [dispatch],
  );

  const removeItemInCart = useCallback(
    (productVariation) => {
      const target = samples.find(
        (sample) => sample.productVariation.id === productVariation.id,
      );

      if (target) {
        event("remove_from_cart", {
          maker_id: target.product.maker_id,
          items: [convert(target)],
        });
      }
      dispatch(removeSample(productVariation));
    },
    [dispatch, event, convert, samples],
  );

  const removeAllItemsInCart = useCallback(() => {
    dispatch(clearAllSamples());
  }, [dispatch]);

  const setQuestionnaireForOrder = useCallback(
    (questionnaire) => {
      dispatch(setQuestionnaire(questionnaire));
    },
    [dispatch],
  );

  const removeQuestionnaireForOrder = useCallback(() => {
    dispatch(clearQuestionnaire());
  }, [dispatch]);

  const setOrder = useCallback(
    (order) => {
      dispatch(setOrderOnStore(order));
    },
    [dispatch],
  );

  const clearOrder = useCallback(() => {
    dispatch(clearOrderOnStore());
  }, [dispatch]);

  const reset = useCallback(() => {
    removeAllItemsInCart();
    removeQuestionnaireForOrder();
  }, [removeAllItemsInCart, removeQuestionnaireForOrder]);

  const orderItems = useMemo<OrderCreateParameterOrderItemsInner[]>(() => {
    return samples.map<OrderCreateParameterOrderItemsInner>(
      ({
        quantity,
        productVariation,
        additional_data,
        board_id,
        question_choice_ids,
      }) => {
        return {
          sample_item_id: productVariation.sample_item?.id,
          product_variation_id: productVariation.id,
          quantity,
          additional_data,
          question_choice_ids,
          board_id,
        };
      },
    );
  }, [samples]);

  const hasItems = useMemo(() => samples.length > 0, [samples]);

  const productVariationIds = useMemo(
    () => samples.map((s) => s.productVariation.id),
    [samples],
  );

  // 同じアイテムがないかをチェックする
  const hasSameItem = useCallback(
    (productVariation: ProductVariationWithSample) => {
      return samples.some(
        (sample) => sample.productVariation.id === productVariation.id,
      );
    },
    [samples],
  );

  // 同じアイテムがないかをチェックする
  const hasSameSampleItem = useCallback(
    (productVariation: ProductVariationWithSample) => {
      // サンプルがひもづいていない場合は
      // プロダクトバリエーションのidで比較する
      if (!productVariation.sample_item) {
        return samples.some(
          (sample) => sample.productVariation.id === productVariation.id,
        );
      }

      return samples.some(
        (sample) =>
          productVariation.sample_item.id ===
          sample.productVariation.sample_item?.id,
      );
    },
    [samples],
  );

  // メーカー直送サンプル
  const directSamples = useMemo(
    () =>
      samples.filter(
        (sample) =>
          sample.productVariation.sample_request_kind ===
          SampleRequestKind.DIRECT,
      ),
    [samples],
  );

  // TSSサンプル
  const indirectSamples = useMemo(
    () =>
      samples.filter(
        (sample) =>
          sample.productVariation.sample_request_kind ===
            SampleRequestKind.INDIRECT || !!sample.productVariation.sample_item,
      ),
    [samples],
  );

  // TSSサンプルの数量を計算したオブジェクト(idはサンプルid)
  const indirectQuantities = useMemo<Record<string, number>>(
    () =>
      calculateQuantities(indirectSamples, (sample) =>
        getSampleIdentifier(sample, false),
      ),
    [indirectSamples],
  );

  // メーカー直送の数量を計算したオブジェクト(idはプロダクトバリエーションid)
  const directQuantities = useMemo<Record<string, number>>(
    () =>
      calculateQuantities(directSamples, (sample) =>
        getSampleIdentifier(sample, true),
      ),
    [directSamples],
  );

  const organizedDirect = useMemo(
    () => organizeSamplesByMakerId(directSamples),
    [directSamples],
  );

  const organizedIndirect = useMemo(
    () => organizeSamplesByMakerId(indirectSamples),
    [indirectSamples],
  );

  // 在庫切れのサンプルを引数に、カート内とてらしあわせOutOfStockSampleの配列を返す
  const getOutOfStockSamples = useCallback(
    (outOfStockSampleItems: SampleItemEntity[]) => {
      return outOfStockSampleItems.map((outOfStockSampleItem) => {
        const target = indirectSamples.find(
          (sample) =>
            sample.productVariation.sample_item.id === outOfStockSampleItem.id,
        );

        return {
          sample: target,
          currentStockCount: outOfStockSampleItem.stock_count,
        };
      });
    },
    [indirectSamples],
  );

  // カートに入っているサンプルの個数
  const quantity = useMemo(() => {
    return samples.reduce((acc, cur) => acc + cur.quantity, 0);
  }, [samples]);

  // 最も最近カートの中身でアップデート（追加・数量変更）されたアイテム
  const latestItem = useMemo(() => {
    const parsedObjects = samples
      .map((sample) => ({
        ...sample,
        compare_at: new Date(sample.updated_at), // 比較用Date
      }))
      .sort((a, b) => b.compare_at.getTime() - a.compare_at.getTime());

    return parsedObjects[0];
  }, [samples]);

  // 指定のプロダクトがカートに入っているかどうかを確認し、重複の有無とカートに入れる場合の数量を返す
  const checkDuplicateBeforeAddToCart = useCallback(
    (productVariation) => {
      const sampleInCart = samples.find(
        (sample) => sample.productVariation.id === productVariation.id,
      );

      if (sampleInCart) {
        // 同じ商品が既にカート内にあり、数量がSKU上限数に達している場合アラートを表示して処理を終了する
        if (
          sampleInCart &&
          sampleInCart.quantity >=
            sampleInCart.product.maker.max_quantity_per_sku
        ) {
          throw new Error(
            getMaxQuantityPerSkuErrorMessage(
              sampleInCart.product.maker.max_quantity_per_sku,
            ),
          );
        }

        return { duplicate: true, quantity: sampleInCart.quantity + 1 };
      }

      return { duplicate: false, quantity: 1 };
    },
    [samples],
  );

  return {
    samplesInCart: samples,
    orderItems,
    questionnaire,
    addSampleToCart,
    removeItemInCart,
    changeOrderCountOfSampleInCart,
    removeAllItemsInCart,
    setQuestionnaireForOrder,
    reset,
    hasSameItem,
    hasSameSampleItem,
    getOutOfStockSamples,
    checkDuplicateBeforeAddToCart,
    hasItems,
    productVariationIds,
    quantity,
    latestItem,
    order,
    setOrder,
    clearOrder,

    indirectQuantities,
    directQuantities,
    organizedDirect,
    organizedIndirect,
  };
};
export default useCart;
